import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calculator, Users, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { MarketingHeader, MarketingFooter, BookCTA } from "@/components/marketing";

const TEAL = "#22D3C5";

const fmt = (n) =>
  new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(isFinite(n) ? n : 0);

function Field({ label, value, onChange, placeholder }) {
  return (
    <div className="mb-5">
      <label className="block text-sm font-medium mb-2 text-foreground/90">{label}</label>
      <input
        type="number"
        inputMode="numeric"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`calc-input-${label}`}
        className="w-full h-12 px-4 rounded-xl bg-secondary border border-border text-foreground text-base outline-none transition-colors focus:border-[color:var(--teal)] placeholder:text-muted-foreground/60"
        style={{ "--teal": TEAL }}
      />
    </div>
  );
}

export default function BudgetCalculator() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [tab, setTab] = useState("budget");

  // Tab 1 - find budget
  const [audience, setAudience] = useState("");
  const [freq1, setFreq1] = useState("4");
  const [cpm1, setCpm1] = useState("80");
  const budget = useMemo(() => {
    const a = parseFloat(audience) || 0, f = parseFloat(freq1) || 0, c = parseFloat(cpm1) || 0;
    return (a * f * c) / 1000;
  }, [audience, freq1, cpm1]);

  // Tab 2 - find reach
  const [budgetIn, setBudgetIn] = useState("");
  const [freq2, setFreq2] = useState("4");
  const [cpm2, setCpm2] = useState("80");
  const reach = useMemo(() => {
    const b = parseFloat(budgetIn) || 0, f = parseFloat(freq2) || 0, c = parseFloat(cpm2) || 0;
    return c > 0 && f > 0 ? (b * 1000) / (c * f) : 0;
  }, [budgetIn, freq2, cpm2]);

  const tabs = [
    { id: "budget", label: "Find budget", Icon: Wallet },
    { id: "reach", label: "Find målgruppe", Icon: Users },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden" data-testid="calculator-page">
      <div className="grain" />
      <MarketingHeader />

      <section className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-8 pt-16 pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-10" data-testid="calc-back-link">
          <ArrowLeft className="h-4 w-4" /> Tilbage til forsiden
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Copy */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Annonceberegner</p>
            <h1 className="font-display text-3xl sm:text-5xl font-medium tracking-tight text-balance leading-tight">
              Find det rigtige <span style={{ color: TEAL }}>annoncebudget</span>
            </h1>
            <p className="text-muted-foreground mt-6 leading-relaxed">
              De fleste kampagner rammer ved siden af på grund af to klassiske fejl: Enten ser målgruppen jeres annoncer for få gange, så folk ikke når at huske brandet - eller også rammer I målgruppen for ofte, hvor de ekstra visninger alligevel ikke flytter noget og burde være brugt på nye potentielle kunder.
            </p>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Brug beregneren til at finde balancen: Regn ud, hvad det koster at nå jeres målgruppe med den rette frekvens - eller hvor mange I realistisk kan nå med jeres budget.
            </p>
            <div className="mt-8 flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
              <Calculator className="h-5 w-5 shrink-0" style={{ color: TEAL }} />
              <p className="text-sm text-muted-foreground">CPM = pris pr. 1.000 visninger. Frekvens = hvor mange gange hver person i snit ser jeres annonce pr. måned.</p>
            </div>
          </motion.div>

          {/* Calculator */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-2xl border border-border bg-card p-6 sm:p-8" style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.04), 0 24px 50px -34px rgba(17,17,17,0.2)" }}>
            {/* Tabs */}
            <div className="flex mb-8 border-b border-border">
              {tabs.map((t) => {
                const active = tab === t.id;
                return (
                  <button key={t.id} onClick={() => setTab(t.id)} data-testid={`calc-tab-${t.id}`}
                    className={`flex-1 flex items-center justify-center gap-2 pb-4 text-sm font-medium transition-all ${active ? "text-foreground" : "text-muted-foreground hover:text-foreground/80"}`}
                    style={active ? { borderBottom: `3px solid ${TEAL}`, marginBottom: "-1px" } : {}}>
                    <t.Icon className="h-4 w-4" /> {t.label}
                  </button>
                );
              })}
            </div>

            {tab === "budget" ? (
              <div data-testid="calc-tab-budget-content">
                <Field label="Målgruppestørrelse (antal personer)" value={audience} onChange={setAudience} placeholder="F.eks. 20.000" />
                <Field label="Mål for frekvens (pr. md.)" value={freq1} onChange={setFreq1} placeholder="4" />
                <Field label="Forventet CPM (kr.)" value={cpm1} onChange={setCpm1} placeholder="80" />
                <div className="mt-7 rounded-2xl p-6 text-center border" style={{ backgroundColor: "rgba(34,211,197,0.08)", borderColor: "rgba(34,211,197,0.35)" }}>
                  <p className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground/70">Anbefalet månedsbudget</p>
                  <p className="font-display text-4xl font-medium mt-2" style={{ color: TEAL }} data-testid="calc-out-budget">{fmt(budget)} kr.</p>
                </div>
              </div>
            ) : (
              <div data-testid="calc-tab-reach-content">
                <Field label="Dit budget (kr.)" value={budgetIn} onChange={setBudgetIn} placeholder="F.eks. 4.000" />
                <Field label="Mål for frekvens (pr. md.)" value={freq2} onChange={setFreq2} placeholder="4" />
                <Field label="Forventet CPM (kr.)" value={cpm2} onChange={setCpm2} placeholder="80" />
                <div className="mt-7 rounded-2xl p-6 text-center border" style={{ backgroundColor: "rgba(34,211,197,0.08)", borderColor: "rgba(34,211,197,0.35)" }}>
                  <p className="text-[10.5px] uppercase tracking-[0.16em] text-muted-foreground/70">Realistisk målgruppe (reach)</p>
                  <p className="font-display text-4xl font-medium mt-2" style={{ color: TEAL }} data-testid="calc-out-reach">{fmt(reach)} personer</p>
                  <p className="text-xs text-muted-foreground mt-2">med den valgte frekvens</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <BookCTA />
      <MarketingFooter />
    </div>
  );
}
